// External libraries
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Components
import { Heading, TypographyType, TypographyColor, Body, BodyColor, BodyType, Button, ButtonSize, ButtonTheme, ButtonVariant, InfoPanel } from "../../ui-elements"
import { POIInfo } from "./POIInfo"
import { POILineChart } from "./POILineChart";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { cifTypeState, compareListState, geoState, isCatchmentAvailableState, ptypeState, selectedTabState, selPOICategories, spinnerState, userCurrencyState } from "../../states";

// Utilities
import { basicCifProps, CATEGORY_ORDER, RouteConstants } from "../../constants";
import * as Constants from '../../utils/constants/Constants';
import { useCIFService, useCompareService } from "../../services";
import { getNonPoiBgColor, getNonPoiIconColor, getParentGeoId, isAddedToCompare, useHandleError, useMapHelpers } from "../../helpers";
import { WorkInProgressPoI } from "./WorkInProgressPoI";
import { RequestData } from ".";
import { AddToCompare } from ".";

export const RightSideBar: React.FC<{ setIsPoiOverlapped: React.Dispatch<React.SetStateAction<boolean>> }> = ({ setIsPoiOverlapped }) => {
    const navigate = useNavigate();
    const compareService = useCompareService();
    const [searchParams] = useSearchParams();
    const cifService = useCIFService();
    const { getCurrencyWithSymbol } = useMapHelpers();
    const { getErrorMsg } = useHandleError();
    const selectedGeo = useRecoilValue(geoState);
    const selCurrency = useRecoilValue(userCurrencyState);
    const selCategoryList = useRecoilValue(selPOICategories);
    const cifType = useRecoilValue(cifTypeState);
    const setSpinner = useSetRecoilState(spinnerState);
    const setIsCatchmentAvailable = useSetRecoilState(isCatchmentAvailableState);
    const [isOpenDrawer, setIsOpenDrawer] = useState(false);
    const compareList = useRecoilValue(compareListState);
    const selectedTab = useRecoilValue(selectedTabState);
    const [cif, setCif] = useState<{ basicCif: basicCifProps, poi: any, poiByYear: { year: number, value: number, averageValue: number, description: string, }[], cagrText: any[] } | null>(null);
    const pType = useRecoilValue(ptypeState);

    const geoLevelForCompare = useMemo(() => {
        // const { country, state, district, sub_district, village } = selectedGeo;
        // const geo_id = village || sub_district || district || state || country;
        // if (geo_id > 1000000) return "villages"
        // if (geo_id > 100000 && geo_id < 999999) return "pincodes"
        // if (district == geo_id) return 'districts';
        // else if (sub_district == geo_id) return 'subdistricts';

        const geoType = cif?.basicCif?.geoType;
        if (geoType) {
            switch (geoType.toLowerCase()) {
                case 'village':
                    return 'villages';
                case 'sub_district':
                    return 'subdistricts';
                case 'district':
                    return 'districts';
                case 'pincode':
                    return 'pincodes';
                default:
                    return '';
            }
        }
    }, [selectedGeo]);

    const handleClickBtn = () => {
        const { country, state, district, sub_district } = selectedGeo;
        const geo_id = sub_district || district || state || country;
        navigate({
            pathname: RouteConstants.dashboard,
            search: `?${searchParams.toString()}`,
        });

    }

    const getPayload = (geoId: number) => {
        const { parent, type } = getParentGeoId(geoId, selectedGeo, selectedTab);
        return {
            geoId,
            ...selCategoryList,
            currency: selCurrency,
            parentgeoId: parent,
            ...(cif?.basicCif?.geoType === 'pincode' ? { type: "pincode" } : {}),
        }
    }

    const fetchBasicCif = (geo_id: any) => {
        // setSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        cifService.getBasicCif(Number(geo_id))
            .then(res => {
                setCif((prev: any) => ({ ...prev, ['basicCif']: res }));
                setIsCatchmentAvailable(res?.isCatchmentAvailable !== false);
            })
            .catch(error => getErrorMsg(error))
        // .finally(() => setSpinner(prev => ({ ...prev, count: prev.count - 1 })));
    }

    const fetchCifPoiData = (geo_id: any) => {
        // setSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        const { parent, type } = getParentGeoId(Number(geo_id), selectedGeo, selectedTab);
        cifService.getCifPoiData({ ...getPayload(Number(geo_id)), parentgeoId: parent })
            .then(res => {
                setIsPoiOverlapped(res?.Poi?.overlap);
                const catData = res?.catData.sort((a: any, b: any) => CATEGORY_ORDER.indexOf(a.catName) - CATEGORY_ORDER.indexOf(b.catName));
                setCif((prev: any) => ({ ...prev, ['poi']: { ...res, catData } }));
            })
            .catch(error => getErrorMsg(error))
        // .finally(() => setSpinner(prev => ({ ...prev, count: prev.count - 1 })));
    }

    const fetchPoiByYear = (geo_id: any) => {
        // setSpinner(prev => ({ ...prev, count: prev.count + 1 }));
        cifService.getPoiByYear(getPayload(Number(geo_id),))
            .then(res => setCif((prev: any) => ({ ...prev, ['poiByYear']: res.growthIntime?.sort((a: any, b: any) => a.date - b.date) || [], cagrText: res.cagrText })))
            .catch(error => console.log(error))
        // .finally(() => setSpinner(prev => ({ ...prev, count: prev.count - 1 })));
    }

    useEffect(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        fetchBasicCif(geo_id);
    }, [selectedGeo, selCurrency]);

    useEffect(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        if (selCategoryList.category.length > 0 && selCategoryList.subcategory.length > 0) {
            fetchCifPoiData(geo_id);
            fetchPoiByYear(geo_id);
        }
    }, [selectedGeo, selCurrency, selCategoryList]);

    return (
        <div className="h-100 w-100 position-relative">
            {/* Header */}
            <div style={{ height: '6.528vh' }} className="w-100 d-flex justify-content-between align-items-center padding-left-right-5 position-sticky top-0 bg-white z-index-1">
                <div className="d-flex justify-content-start align-items-center width-70 single-poi-info-cardsss position-relative">
                    <Heading
                        title={cif?.basicCif?.region}
                        type={TypographyType.h3}
                        colour={TypographyColor.primary}
                        classname='text-start text-truncate'
                    />
                    <div className="helper-highlight-text-rightMenu  padding-left-right-2 padding-top-bottom-1 highlightshovertext ms-1">
                        <Body
                            type={BodyType.p2}
                            color={BodyColor.white}
                            classname="p-0 text-start"
                        >
                            {cif?.basicCif?.region}
                        </Body>
                    </div>

                    <InfoPanel text={Constants.iButtonText.homePage.cif.india} classname='color-black-0 fs-18 margin-left-2' translateX='translateX(-28%)' />
                </div>

                {selectedGeo.district && cif?.basicCif?.geoLevelNumber &&
                    <AddToCompare
                        isAddedToCompare={isAddedToCompare(cif?.basicCif?.geo_id, compareList, pType)}
                        geoId={Number(cif?.basicCif?.geo_id)}
                        level={cif?.basicCif?.geoLevelNumber}
                        parent_id={Number(selectedGeo.district)}
                        count={compareList?.[geoLevelForCompare!]?.length ?? 0}
                        showNodeWithoutHover={true}
                        tooltipPosition="left"
                    />
                }
            </div>

            {/* Body */}
            <div style={{ height: '79.405vh' }} className="overflow-y-auto position-absolute m-0 p-0 w-100" >
                <div className="row d-flex justify-content-between align-items-center border-bottom bg-white padding-left-right-5 padding-top-0 padding-bottom-5 m-0">
                    <div className="col-6 d-flex justify-content-start align-items-center p-0 m-0">
                        <div className="d-flex justify-content-center align-items-center rounded-circle square-icon-box geo-specific-icon">
                            <span className="material-symbols-outlined fs-20">group</span>
                        </div>
                        <div className="padding-left-right-3 p-0">
                            <Heading
                                title={cif?.basicCif?.totalPopulation}
                                type={TypographyType.h4}
                                colour={TypographyColor.primary}
                                classname='text-start p-0'
                            />
                            <Body
                                type={BodyType.p2}
                                color={BodyColor.secondary}
                                classname="p-0 max-w-95 padding-right-3"
                            >
                                Population
                            </Body>
                        </div>
                    </div>
                    <div className="col-6 d-flex justify-content-start align-items-center p-0 m-0">
                        <div className="d-flex justify-content-center align-items-center rounded-circle square-icon-box geo-specific-icon text-center">
                            <span className="material-symbols-outlined fs-20">location_away</span>
                        </div>
                        <div className="padding-left-right-3">
                            <Heading
                                title={cif?.basicCif?.totalHouseholds}
                                type={TypographyType.h4}
                                colour={TypographyColor.primary}
                                classname='text-start p-0'
                            />
                            <Body
                                type={BodyType.p2}
                                color={BodyColor.secondary}
                                classname="p-0 max-w-95 padding-right-3"
                            >
                                Households
                            </Body>
                        </div>
                    </div>
                </div>
                <div className={`border-bottom padding-5 ${getNonPoiBgColor(cifType)}`}>
                    <div className="padding-bottom-5">
                        <Heading
                            title={getCurrencyWithSymbol(cif?.basicCif?.totalAddressableMarket, selCurrency)}
                            type={TypographyType.h4}
                            colour={TypographyColor.primary}
                            classname='text-start text-truncate p-0'
                        />
                        <div className="p-0 d-flex align-items-center justify-content-start">
                            <Body
                                type={BodyType.p2}
                                color={BodyColor.secondary}
                                classname=""
                            >
                                Core Transaction Value (<b>CTV</b>)
                            </Body>
                            <InfoPanel text={Constants.iButtonText.homePage.cif.coreTransactionValue} classname='color-black-7 fs-14 margin-left-2' />
                        </div>

                    </div>
                    <div className="row d-flex justify-content-between align-items-center m-0 p-0">
                        <div className="col-6 d-flex justify-content-start align-items-center m-0 p-0">
                            <div className={`d-flex justify-content-center align-items-center rounded-circle square-icon-box text-center ${getNonPoiIconColor(cifType)}`}>
                                <span className="material-symbols-outlined fs-20">percent</span>
                            </div>
                            <div className="padding-left-3">
                                <Heading
                                    title={getCurrencyWithSymbol(cif?.basicCif?.averageCtv, selCurrency)}
                                    type={TypographyType.h4}
                                    colour={TypographyColor.primary}
                                    classname='text-start text-truncate p-0'
                                />
                                <Body
                                    type={BodyType.p2}
                                    color={BodyColor.secondary}
                                    classname="p-0 max-w-95 padding-right-3"
                                >
                                    Average Annual CTV
                                </Body>
                            </div>
                        </div>
                        <div className="col-6 d-flex justify-content-start align-items-center m-0 p-0">
                            <div className={`d-flex justify-content-center align-items-center rounded-circle square-icon-box text-center ${getNonPoiIconColor(cifType)}`}>
                                <span className="material-symbols-outlined fs-20">work</span>
                            </div>
                            <div className="padding-left-3">
                                <Heading
                                    title={cif?.basicCif?.eh}
                                    type={TypographyType.h4}
                                    colour={TypographyColor.primary}
                                    classname='text-start text-truncate p-0'
                                />
                                <Body
                                    type={BodyType.p2}
                                    color={BodyColor.secondary}
                                    classname="p-0 max-w-95 padding-right-3"
                                >
                                    Entrepreneurial Households (<b>EH</b>)
                                </Body>
                            </div>
                        </div>
                    </div>
                </div>
                {cif?.poi?.Poi?.show
                    ? <div className={`${cifType.length <= 1 && cifType.includes('PoI') ? 'bg-poi' : 'bg-white'}`}>
                        <POIInfo poi={cif?.poi} isPoIToggled={cifType.length <= 1 && cifType.includes('PoI')} />
                        {cif?.poiByYear && cif?.poiByYear.length > 0 && <POILineChart data={cif?.poiByYear} cagrText={cif.cagrText} />}
                    </div>
                    : <WorkInProgressPoI subTitletext='We are in the process of covering all economically vibrant locations in India. Please let us know if you would like this geography to be covered.' />
                }
            </div>

            {/* Footer */}
            <div style={{ height: '7.752vh' }} className="w-100 position-absolute padding-left-right-5 bg-white m-0 bottom-0 d-flex justify-content-center align-items-center">
                <Button
                    theme={ButtonTheme.primary}
                    variant={ButtonVariant.bordered}
                    type='button'
                    size={ButtonSize.large}
                    onClick={handleClickBtn}
                    classname=''
                >
                    Explore More
                </Button>
            </div>

            <RequestData isOpen={isOpenDrawer} setIsOpen={setIsOpenDrawer} geoName={cif?.basicCif?.region} subHeading={`If you like to request data for “${cif?.basicCif?.region}”, fill the following form to request this. We will engage with you to prioritize this geography of interest, and notify you once the data has been updated.`} purpose="PoI coverage" />
        </div>
    )
}